import type { Swatch } from "./types.js";

export function roundedRectPath(ctx: CanvasRenderingContext2D, swatch: Pick<Swatch, "x" | "y" | "width" | "height" | "radius">): void {
  const r = Math.max(0, Math.min(swatch.radius, swatch.width / 2, swatch.height / 2));
  const x = swatch.x;
  const y = swatch.y;
  const w = swatch.width;
  const h = swatch.height;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function hashNoise(seed: number, x: number, y: number): number {
  let n = seed ^ (x * 374761393) ^ (y * 668265263);
  n = (n ^ (n >>> 13)) * 1274126177;
  return ((n ^ (n >>> 16)) >>> 0) / 0xffffffff;
}

export interface TextureLayers {
  /** Deterministic paper-grain cells, drawn to be blitted with `multiply`. */
  grain: HTMLCanvasElement;
  /** Diagonal highlight/shadow sheen, drawn to be blitted with `soft-light`. */
  sheen: HTMLCanvasElement;
}

const layersCache = new WeakMap<object, TextureLayers>();

/** Draw one swatch's grain cells into the (transparent) grain layer. */
function drawGrain(ctx: CanvasRenderingContext2D, swatch: Swatch): void {
  ctx.save();
  roundedRectPath(ctx, swatch);
  ctx.clip();
  ctx.globalAlpha = 0.11;
  for (let y = Math.floor(swatch.y); y < swatch.y + swatch.height; y += 4) {
    for (let x = Math.floor(swatch.x); x < swatch.x + swatch.width; x += 4) {
      const n = hashNoise(swatch.index + 1, x, y);
      const tone = n > 0.54 ? 40 : 235;
      ctx.fillStyle = `rgb(${tone}, ${tone}, ${tone})`;
      ctx.fillRect(x, y, 3, 3);
    }
  }
  ctx.restore();
}

/** Draw one swatch's sheen gradient into the (transparent) sheen layer. */
function drawSheen(ctx: CanvasRenderingContext2D, swatch: Swatch): void {
  ctx.save();
  roundedRectPath(ctx, swatch);
  ctx.clip();
  const gradient = ctx.createLinearGradient(swatch.x, swatch.y, swatch.x + swatch.width, swatch.y + swatch.height);
  gradient.addColorStop(0, "rgba(255,255,255,0.14)");
  gradient.addColorStop(0.52, "rgba(255,255,255,0)");
  gradient.addColorStop(1, "rgba(40,24,8,0.12)");
  ctx.fillStyle = gradient;
  ctx.fillRect(swatch.x, swatch.y, swatch.width, swatch.height);
  ctx.restore();
}

/**
 * The visual-only paper texture, prebuilt ONCE per template into two
 * transparent full-board layers. The grain/sheen depend only on swatch
 * GEOMETRY (never on the current colors), so a render pass just blits the
 * cached layers with `multiply`/`soft-light`. Drawing the grain as ~17k
 * individual `fillRect`s inside 21 clip layers on every render made the GPU
 * raster of a theme switch stall frame production for ~300–500ms.
 */
export function textureLayersFor(template: {
  imageWidth: number;
  imageHeight: number;
  swatches: readonly Swatch[];
}): TextureLayers | null {
  const cached = layersCache.get(template);
  if (cached) return cached;
  const make = () => {
    const canvas = document.createElement("canvas");
    canvas.width = template.imageWidth;
    canvas.height = template.imageHeight;
    return canvas;
  };
  const grain = make();
  const sheen = make();
  const grainCtx = grain.getContext("2d");
  const sheenCtx = sheen.getContext("2d");
  if (!grainCtx || !sheenCtx) return null;
  for (const swatch of template.swatches) {
    drawGrain(grainCtx, swatch);
    drawSheen(sheenCtx, swatch);
  }
  const layers: TextureLayers = { grain, sheen };
  layersCache.set(template, layers);
  return layers;
}
